import { useEffect, useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import "./ResponsTable.css";
import Divider from "../ui/Divider";
import Button from "../ui/Button";
import Input from "../ui/InputForm";
import { useAuth } from "../../context/AuthContext";

function ResponsTable({ filters, period }) {
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [groupBy, setGroupBy] = useState("aanbieder");
  const [searchTerm, setSearchTerm] = useState("");
  const { tokens, isVerifying, authorizedFetch } = useAuth();

  useEffect(() => {
    if (isVerifying || !tokens?.access) return; // wait until auth state is resolved
    const controller = new AbortController();
    let ignore = false;

    async function loadRespons() {
      try {
        const params = new URLSearchParams({ group_by: groupBy });
        if (filters?.gemeente) params.set("gemeente", filters.gemeente);
        if (filters?.jaar) params.set("jaar", filters.jaar);
        if (period && period !== "all") params.set("period", period);
        const response = await authorizedFetch(`/api/respons_per_groep/?${params}`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Failed to load respons data: ${response.status}`);
        }
        const payload = await response.json();
        if (!ignore) {
          setData(payload);
        }
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        console.error("Failed to fetch respons data", err); // eslint-disable-line no-console
      }
    }

    loadRespons();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [tokens?.access, isVerifying, groupBy, filters?.gemeente, filters?.jaar, period, authorizedFetch]);

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return data;
    return data.filter((row) =>
      (row.organisatie || "").toLowerCase().includes(term)
    );
  }, [data, searchTerm]);

  const totalRespondents = useMemo(
    () => filteredData.reduce((sum, row) => sum + (row.aantal_respondenten || 0), 0),
    [filteredData]
  );

  const columns = [
    {
      id: "select",
      header: () => <input type="checkbox" />,
      cell: () => <input type="checkbox" />,
    },
    { accessorKey: "organisatie", header: "Organisatie" },
    {
      accessorKey: "aantal_respondenten",
      header: "Aantal respondenten",
      cell: (info) => {
        const value = info.getValue() ?? 0;
        return value.toLocaleString();
      },
    },
    {
      id: "percentage_van_totaal",
      header: "% van totaal",
      accessorFn: (row) =>
        totalRespondents
          ? (row.aantal_respondenten / totalRespondents) * 100
          : 0,
      cell: (info) => {
        const value = info.getValue() ?? 0;
        const formatted = `${value.toFixed(1)}%`;
        const width = Math.max(0, Math.min(100, value));
        return (
          <div className="progress-cell">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${width}%` }}
              ></div>
            </div>
            <span>{formatted}</span>
          </div>
        );
      },
    },
    {
      accessorKey: "vragenlijst",
      header: "Vragenlijst",
      cell: (info) => (
        <span className="status-pill active">{info.getValue()}</span>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleExport = () => {
    if (!filteredData.length) return;

    const header = [
      "Groepstype",
      "Organisatie",
      "Aantal respondenten",
      "% van totaal",
      "Vragenlijst",
    ];

    const rows = filteredData.map((row) => {
      const respondents = row.aantal_respondenten ?? 0;
      const perc = totalRespondents
        ? ((respondents / totalRespondents) * 100).toFixed(1) + "%"
        : "0.0%";

      return [
        groupBy,
        row.organisatie ?? "",
        respondents,
        perc,
        row.vragenlijst ?? "",
      ];
    });

    const csv = [header, ...rows]
      .map((line) =>
        line
          .map((value) => {
            const safe = `${value ?? ""}`.replace(/"/g, '""');
            return `"${safe}"`;
          })
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `respons_${groupBy}_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="table-wrapper">
      <h2 className="table-title">Respons per groep</h2>
      <Divider />
      <div className="table-toolbar">
        <div className="left-controls">
          <button
            className={`switch ${groupBy === "aanbieder" ? "active" : ""}`}
            onClick={() => setGroupBy("aanbieder")}
            disabled={groupBy === "aanbieder"}
          >
            Aanbieders
          </button>
          <button
            className={`switch ${groupBy === "gemeente" ? "active" : ""}`}
            onClick={() => setGroupBy("gemeente")}
            disabled={groupBy === "gemeente"}
          >
            Gemeenten
          </button>
        </div>

        <div className="right-controls">
        <div className="filter-item search-item">
            <Input
              label="Zoek op groep"
              name="group-search"
              type="text"
              placeholder="Zoek op groep"
              leftIcon="/icons/glass.png"
              labelHelpIcon="/icons/help-circle.png"
              rightIcon="/icons/cmdk.png"
              wrapperClassName="filter-input-wrapper m0"
              inputClassName="filter-input"
              noLabel={true}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
        </div>
          <Button
            variant="secondary"
            icon="/icons/filter-lines.png"
            iconPosition="left"
            onClick={handleExport}
            disabled={!filteredData.length}
          >
            Export
          </Button>
        </div>
      </div>
      <Divider />

      <table className="styled-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  style={{ cursor: "pointer" }}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                  {{
                    asc: " 🔼",
                    desc: " 🔽",
                  }[header.column.getIsSorted()] ?? null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination footer */}
      <div className="table-footer">
        <span>
          Pagina {table.getState().pagination.pageIndex + 1} van{" "}
          {table.getPageCount()}
        </span>
        <div className="pagination-buttons">
          <Button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            variant="secondary"
          >
            Vorige
          </Button>
          <Button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            variant="secondary"
          >
            Volgende
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ResponsTable;
